if (!customElements.get('product-info')) {
  const INVENTORY_DOM_SUFFIXES = ['', '-mobile'];

  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      pickInventoryMarkupElement(htmlDoc) {
        return (
          htmlDoc.querySelector(`.product__info-wrapper #Inventory-${this.sectionId}`) ||
          htmlDoc.getElementById(`Inventory-${this.sectionId}-mobile`) ||
          htmlDoc.getElementById(`Inventory-${this.sectionId}`)
        );
      }

      syncInventoryFromFetchedHtml(htmlDoc) {
        const source = this.pickInventoryMarkupElement(htmlDoc);
        if (!source) return;
        const shouldHide = source.innerText.trim() === '';
        const sectionKey = this.dataset.section;
        for (const suffix of INVENTORY_DOM_SUFFIXES) {
          const destination = this.querySelector(`#Inventory-${sectionKey}${suffix}`);
          if (!destination) continue;
          destination.innerHTML = source.innerHTML;
          destination.classList.toggle('hidden', shouldHide);
        }
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }

        this.toggleOutOfStockVariantClass(this.currentVariant);
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      toggleOutOfStockVariantClass(variant) {
        const productContainer = this.querySelector('.product');
        if (!productContainer) return;
        productContainer.classList.toggle('outofstock-variant', !!variant && variant.available === false);
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues, resolvedVariantId } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        // When switching products (e.g. combined listings) resolvedVariantId refers to the
        // previous product's variants, so it can't be reused there.
        const variantIdOverride = shouldSwapProduct ? null : resolvedVariantId;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage,
            variantIdOverride
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
          this.toggleOutOfStockVariantClass(this.currentVariant);
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);
          this.currentVariant = variant;

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            this.toggleOutOfStockVariantClass({ available: false });
            return;
          }

          this.toggleOutOfStockVariantClass(variant);
          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          this.syncInventoryFromFetchedHtml(html);
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Badges-mobile', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');
          // Keep the icon-with-text shipping line's delivery estimate in sync per variant
          updateSourceFromDestination('IconWithTextDelivery');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          // Sync line item properties[_inventory_quantity] with current variant inventory
          const inventorySource = this.pickInventoryMarkupElement(html);
          const qtyNode = inventorySource?.querySelector('.js-variant-inventory-quantity');
          if (qtyNode) {
            const inventoryQty = qtyNode.dataset.inventoryQuantity ?? '';

            document
              .querySelectorAll(
                `#product-form-${this.sectionId} input[name="properties[_inventory_quantity]"], #product-form-installment-${this.sectionId} input[name="properties[_inventory_quantity]"]`
              )
              .forEach((input) => {
                input.value = inventoryQty;
              });

            document
              .querySelectorAll(
                `.sticky-atc-bar [data-section="${this.sectionId}"] form[is="product-form"] input[name="properties[_inventory_quantity]"]`
              )
              .forEach((input) => {
                input.value = inventoryQty;
              });
          }

          // Sync variant logistics metafields into line item properties
          const logisticsNode = html.querySelector('.js-variant-logistics-metafields');
          if (logisticsNode) {
            const fulfillmentAttributes = logisticsNode.dataset.fulfillmentAttributes ?? '';
            const shipmentDimensions = logisticsNode.dataset.shipmentDimensions ?? '';
            const expectedInbound = logisticsNode.dataset.expectedInbound ?? '';
            const deliveryTime = logisticsNode.dataset.deliveryTime ?? '';

            document
              .querySelectorAll(
                `#product-form-${this.sectionId} input[name="properties[_expected_inbound]"], #product-form-installment-${this.sectionId} input[name="properties[_expected_inbound]"], .sticky-atc-bar [data-section="${this.sectionId}"] form[is="product-form"] input[name="properties[_expected_inbound]"]`
              )
              .forEach((input) => {
                input.value = expectedInbound;
              });

            document
              .querySelectorAll(
                `#product-form-${this.sectionId} input[name="properties[_delivery_time]"], #product-form-installment-${this.sectionId} input[name="properties[_delivery_time]"], .sticky-atc-bar [data-section="${this.sectionId}"] form[is="product-form"] input[name="properties[_delivery_time]"]`
              )
              .forEach((input) => {
                input.value = deliveryTime;
              });

            document
              .querySelectorAll(
                `#product-form-${this.sectionId} input[name="properties[_logistics.fulfillment_attributes]"], #product-form-installment-${this.sectionId} input[name="properties[_logistics.fulfillment_attributes]"]`
              )
              .forEach((input) => {
                input.value = fulfillmentAttributes;
              });

            document
              .querySelectorAll(
                `#product-form-${this.sectionId} input[name="properties[_logistics.shipment_dimensions]"], #product-form-installment-${this.sectionId} input[name="properties[_logistics.shipment_dimensions]"]`
              )
              .forEach((input) => {
                input.value = shipmentDimensions;
              });

            document
              .querySelectorAll(
                `.sticky-atc-bar [data-section="${this.sectionId}"] form[is="product-form"] input[name="properties[logistics.fulfillment_attributes]"]`
              )
              .forEach((input) => {
                input.value = fulfillmentAttributes;
              });

            document
              .querySelectorAll(
                `.sticky-atc-bar [data-section="${this.sectionId}"] form[is="product-form"] input[name="properties[logistics.shipment_dimensions]"]`
              )
              .forEach((input) => {
                input.value = shipmentDimensions;
              });
          }

          // Sync variant campaign-code metafields (price_source_promo / price_source_competitive) into line item properties
          const campaignNode = html.querySelector('.js-variant-campaign-metafields');
          if (campaignNode) {
            const priceSourcePromo = campaignNode.dataset.priceSourcePromo ?? '';
            const priceSourceCompetitive = campaignNode.dataset.priceSourceCompetitive ?? '';

            document
              .querySelectorAll(
                `#product-form-${this.sectionId} input[name="properties[_price_source_promo]"], #product-form-installment-${this.sectionId} input[name="properties[_price_source_promo]"], .sticky-atc-bar [data-section="${this.sectionId}"] form[is="product-form"] input[name="properties[_price_source_promo]"]`
              )
              .forEach((input) => {
                input.value = priceSourcePromo;
              });

            document
              .querySelectorAll(
                `#product-form-${this.sectionId} input[name="properties[_price_source_competitive]"], #product-form-installment-${this.sectionId} input[name="properties[_price_source_competitive]"], .sticky-atc-bar [data-section="${this.sectionId}"] form[is="product-form"] input[name="properties[_price_source_competitive]"]`
              )
              .forEach((input) => {
                input.value = priceSourceCompetitive;
              });
          }

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut
          );

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            })
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false, variantIdOverride = null) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (variantIdOverride) {
          // A specific variant was resolved client-side (e.g. to auto-pick the nearest available
          // combination), so request it directly instead of letting Shopify resolve option_values.
          params.push(`variant=${variantIdOverride}`);
        } else if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.innerHTML = FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        const sectionKey = this.dataset.section;
        for (const suffix of INVENTORY_DOM_SUFFIXES) {
          const inventory = this.querySelector(`#Inventory-${sectionKey}${suffix}`);
          if (inventory) inventory.classList.add('hidden');
        }
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
            this.updateAddToCartCartQty(parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }

      updateAddToCartCartQty(parsedHTML) {
        const formId = `product-form-${this.sectionId}`;
        const parsedForm = parsedHTML.getElementById(formId);
        const currentForm = document.getElementById(formId);
        if (!parsedForm || !currentForm) return;

        const parsedQtyEl = parsedForm.querySelector('.buy-buttons__cart-qty');
        const currentTextEl = currentForm.querySelector('[name="add"] > span');
        if (!currentTextEl) return;

        const currentQtyEl = currentTextEl.querySelector('.buy-buttons__cart-qty');

        // If the new HTML doesn't include the qty badge (qty=0), remove it from current DOM.
        if (!parsedQtyEl) {
          currentQtyEl?.remove();
          return;
        }

        // Otherwise ensure it exists and matches the latest HTML.
        if (currentQtyEl) {
          currentQtyEl.textContent = parsedQtyEl.textContent;
        } else {
          currentTextEl.insertAdjacentHTML('beforeend', parsedQtyEl.outerHTML);
        }
      }
    }
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal,
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    }
  );
}

/**
 * Initialize review badge click handlers
 * When .review-badges is clicked, it will scroll to and open the .review-tab accordion
 */
(function initReviewBadgeHandlers() {
  const handledBadges = new WeakSet();
  
  function attachHandlers() {
    const reviewBadges = document.querySelectorAll('.review-badges:not([data-review-handler-attached])');
    
    reviewBadges.forEach((badge) => {
      // Mark as handled to prevent duplicate listeners
      badge.setAttribute('data-review-handler-attached', 'true');
      
      badge.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Find the review tab element
        const reviewTab = document.querySelector('.review-tab');
        if (!reviewTab) {
          console.warn('Review tab not found. Make sure to add "review-tab" as a custom class to your collapsible tab block.');
          return;
        }
        
        // Find the details/accordion element inside the review tab
        const accordionDetails = reviewTab.querySelector('details[is="accordion-group"]');
        if (!accordionDetails) {
          console.warn('Accordion details not found in review tab');
          return;
        }
        
        // Calculate offset for smooth scroll (accounting for sticky headers if any)
        const headerOffset = 100; // Adjust this value based on your header height
        const elementPosition = reviewTab.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        
        // Scroll to the review tab
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        
        // Wait a bit for scroll to start, then open the accordion
        setTimeout(() => {
          if (!accordionDetails.open) {
            accordionDetails.open = true;
          }
        }, 300);
      });
    });
  }
  
  // Initialize on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachHandlers);
  } else {
    attachHandlers();
  }
  
  // Re-initialize when product info is updated (for dynamic content)
  if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
    FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
      () => {
        setTimeout(attachHandlers, 100);
      }
    );
  }
  
  // Also listen for mutations in case content is added dynamically
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(() => {
      attachHandlers();
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
})();

/**
 * When .tax-shipping-anchor is clicked, scroll to and open the shipping accordion.
 */
(function initTaxShippingAnchorHandlers() {
  function findShippingAccordionWrapper() {
    return (
      document.querySelector('[data-shipping-accordion-target]') ||
      document.querySelector('#product-shipping-info') ||
      document.querySelector('.tax_anchor')
    );
  }

  function openShippingAccordion(wrapper) {
    const accordionDetails = wrapper.querySelector('details[is="accordion-group"]') || wrapper.querySelector('details');
    if (!accordionDetails) return;

    const scrollToWrapper = () => {
      const top = wrapper.getBoundingClientRect().top + window.scrollY - 300;
      window.scrollTo({ top, behavior: 'smooth' });
    };

    if (!accordionDetails.open) {
      const originalHandleAfterToggle = accordionDetails.handleAfterToggle;
      accordionDetails.handleAfterToggle = function () {};
      accordionDetails.open = true;
      setTimeout(() => {
        accordionDetails.handleAfterToggle = originalHandleAfterToggle;
        scrollToWrapper();
      }, 300);
      return;
    }

    scrollToWrapper();
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest('.tax-shipping-anchor');
    if (!link) return;

    event.preventDefault();

    const wrapper = findShippingAccordionWrapper();
    if (!wrapper) return;

    openShippingAccordion(wrapper);
  });
})();
